/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { rendererIconByName, truthyCellRenderer } from '@gh/common-internal-shared';
import { DateTimeTypes, IS_REGION_AU_OR_NZ, NumberTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  buildDateTimeColumn,
  buildNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
  GridSelectedRowChangeEvent,
} from '@gh/core-ui-grid';
import { compact } from 'lodash';
import { SfCompletionQuoteListItem } from '../../../resources/lead.model';

@Component({
  standalone: false,
  selector: 'gh-complete-sale-quotes-step',
  templateUrl: 'complete-sale-quotes-step.component.html',
  styleUrls: ['complete-sale-quotes-step.component.scss'],
})
export class CompleteSaleQuotesStepComponent implements OnInit {
  @Input() quotes: SfCompletionQuoteListItem[];

  @Output() selectedChange = new EventEmitter<SfCompletionQuoteListItem>();

  selectedQuote?: SfCompletionQuoteListItem;

  gridDefinition: GridDefinition;

  constructor(private gridDefinitionFactory: GridDefinitionFactory,
              private translations: TranslationService) {

  }

  @Input()
  set selected(quote: SfCompletionQuoteListItem) {
    this.selectedQuote = quote;
  }

  get selectedRowId(): Maybe<ID> {
    const { selectedQuote } = this;
    return selectedQuote && selectedQuote.id;
  }

  ngOnInit(): void {
    this.gridDefinition = this.buildGridDefinition();
  }

  onSelectedRowChange(event: GridSelectedRowChangeEvent<SfCompletionQuoteListItem>): void {
    if (event.data != null) {
      this.selectedChange.emit(event.data);
    }
  }

  private buildGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual(
      compact([
        { sortable: false, field: 'rfqNumber', width: 100, headerName: this.translations.get('lbl_quote_no') },
        {
          sortable: false,
          field: 'approvedDate',
          width: 120,
          headerName: this.translations.get('lbl_approved'),
          ...buildDateTimeColumn(DateTimeTypes.ShortDate),
        },
        {
          sortable: false,
          field: 'approvedBy',
          width: 120,
          headerName: this.translations.get('lbl_approved_by'),
        },
        {
          sortable: false,
          sort: 'desc',
          field: 'lastUpdated',
          width: 120,
          headerName: this.translations.get('lbl_last_updated'),
          ...buildDateTimeColumn(DateTimeTypes.ShortDate),
        },
        { sortable: false, field: 'housePlanCode', width: 120, headerName: this.translations.get('lbl_hp_one') },
        {
          sortable: false,
          field: 'isDeleted',
          width: 80,
          headerName: this.translations.get('lbl_deleted'),
          cellRenderer: truthyCellRenderer(rendererIconByName('done')),
        },
        IS_REGION_AU_OR_NZ && {
          sortable: false,
          field: 'rfqPrice2',
          width: 120,
          headerName: this.translations.get('lbl_price_ex_gst'),
          ...buildNumberColumn(NumberTypes.Price),
        },
        {
          sortable: false,
          field: 'rfqPrice1',
          width: 120,
          headerName: this.translations.get(IS_REGION_AU_OR_NZ ? 'lbl_price_incl_gst' : 'lbl_price'),
          ...buildNumberColumn(NumberTypes.Price),
        },
      ]),
      {
        rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
        getRowId: (params) => String(params.data.id)
      },
    );
  }
}
